/**
 * Calculates the next occurrence date for a recurring booking.
 *
 * For non-recurring (default) events, it checks if the event (derived from the booking start's day and time)
 * falls within the selected month. If not, it then checks the next month.
 *
 * @param {string|Date} selectedMonth - The current selected month (as a date or parsable string).
 * @param {string|Date} bookingTypeStart - The start date/time for the booking.
 * @param {string} recurringType - The recurrence type ('day', 'week', 'month', 'year', or default for non-recurring).
 * @param {number} recurringInterval - The interval multiplier for the recurrence (default is 1).
 * @param {string|null} bookingTypeEnd - Optional end date for the recurrence.
 * @returns {Date|null} - The next occurrence date or null if no further occurrence.
 */
export function getNextOccurrence(selectedMonth, bookingTypeStart, recurringType, recurringInterval = 1, bookingTypeEnd = null) {
  // Convert selectedMonth and bookingTypeStart to Date objects.
  const currentMonth = new Date(selectedMonth);
  const bookingTime = new Date(bookingTypeStart);
  
  // If an end date is provided and currentMonth is already past it, exit early.
  if (bookingTypeEnd && new Date(bookingTypeEnd) < currentMonth) {
    return null;
  }
  
  let nextOccurrence;

  // Helper function that returns an occurrence date in the specified year and month,
  // preserving the day and time from bookingTime. If the day doesn't exist (e.g., Feb 30),
  // it uses the last day of that month.
  function getOccurrenceForMonth(year, month) {
    const day = bookingTime.getDate();
    const hours = bookingTime.getHours();
    const minutes = bookingTime.getMinutes();
    const seconds = bookingTime.getSeconds();
    // Determine the last valid day for the month.
    const lastDayOfMonth = new Date(year, month + 1, 0).getDate();
    const validDay = Math.min(day, lastDayOfMonth);
    return new Date(year, month, validDay, hours, minutes, seconds);
  }
  
  // Calculate next occurrence based on the recurrence type.
  switch (recurringType) {
    case 'day': {
      const msPerDay = 24 * 60 * 60 * 1000;
      // Determine how many intervals have elapsed.
      const daysElapsed = Math.ceil((currentMonth - bookingTime) / (msPerDay * recurringInterval));
      nextOccurrence = new Date(bookingTime);
      nextOccurrence.setDate(bookingTime.getDate() + Math.max(daysElapsed, 1) * recurringInterval);
      break;
    }
    case 'week': {
      const msPerWeek = 7 * 24 * 60 * 60 * 1000;
      const weeksElapsed = Math.ceil((currentMonth - bookingTime) / (msPerWeek * recurringInterval));
      nextOccurrence = new Date(bookingTime);
      nextOccurrence.setDate(bookingTime.getDate() + Math.max(weeksElapsed, 1) * 7 * recurringInterval);
      break;
    }
    case 'month': {
      // Calculate the total months elapsed between bookingTime and currentMonth.
      const monthsElapsed = (currentMonth.getFullYear() - bookingTime.getFullYear()) * 12 +
                            (currentMonth.getMonth() - bookingTime.getMonth());
      const intervals = Math.ceil(monthsElapsed / recurringInterval);
      nextOccurrence = new Date(bookingTime);
      nextOccurrence.setMonth(bookingTime.getMonth() + Math.max(intervals, 1) * recurringInterval);
      break;
    }
    case 'year': {
      const yearsElapsed = currentMonth.getFullYear() - bookingTime.getFullYear();
      const intervals = Math.ceil(yearsElapsed / recurringInterval);
      nextOccurrence = new Date(bookingTime);
      nextOccurrence.setFullYear(bookingTime.getFullYear() + Math.max(intervals, 1) * recurringInterval);
      break;
    }
    default: {
      // For non-recurring (default) events, we want to use the day and time from bookingTime
      // and check if the occurrence in the selected month is valid.
      
      // Get the occurrence for the selected month.
      const occurrenceInSelectedMonth = getOccurrenceForMonth(currentMonth.getFullYear(), currentMonth.getMonth());
      
      // Define the boundaries for the selected month.
      const monthStart = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1, 0, 0, 0);
      const monthEnd = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0, 23, 59, 59, 999);
      
      // If the computed occurrence falls within the selected month, use it.
      if (occurrenceInSelectedMonth >= monthStart && occurrenceInSelectedMonth <= monthEnd) {
        // Also check if bookingTypeEnd is provided.
        if (bookingTypeEnd && occurrenceInSelectedMonth > new Date(bookingTypeEnd)) {
          return null;
        }
        nextOccurrence = occurrenceInSelectedMonth;
      } else {
        // Otherwise, try the next month.
        const nextMonthOccurrence = getOccurrenceForMonth(currentMonth.getFullYear(), currentMonth.getMonth() + 1);
        // Boundaries for the next month.
        const nextMonthStart = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1, 0, 0, 0);
        const nextMonthEnd = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 2, 0, 23, 59, 59, 999);
        if (nextMonthOccurrence >= nextMonthStart && nextMonthOccurrence <= nextMonthEnd) {
          if (bookingTypeEnd && nextMonthOccurrence > new Date(bookingTypeEnd)) {
            return null;
          }
          nextOccurrence = nextMonthOccurrence;
        } else {
          return null;
        }
      }
      break;
    }
  }
  
  // Final check: if an end date is provided and the computed occurrence exceeds it, return null.
  if (bookingTypeEnd && nextOccurrence > new Date(bookingTypeEnd)) {
    return null;
  }
  
  return nextOccurrence;
}
